import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'

// Layout
import Navbar from '@components/layout/Navbar'
import Footer from '@components/layout/Footer'
import ChatAgent from '@components/ui/ChatAgent'

// ─── Lazy-loaded page chunks ───────────────────────────────────────────────────
// Each page becomes its own JS chunk at build time (see vite.config.js manualChunks).
const Landing   = lazy(() => import('@pages/Landing'))
const Login     = lazy(() => import('@pages/Login'))
const Register  = lazy(() => import('@pages/Register'))
const Dashboard = lazy(() => import('@pages/Dashboard'))
const Profile   = lazy(() => import('@pages/Profile'))
const Payment   = lazy(() => import('@pages/Payment'))
const NotFound  = lazy(() => import('@pages/NotFound'))

// ─── Full-screen loading fallback ─────────────────────────────────────────────

/**
 * Shown by Suspense while a lazy page chunk is being fetched.
 * Dark background matches the app's base colour (#0a0a0f) so there is no
 * flash of white before the page paint.
 */
function LoadingFallback() {
  return (
    <div style={styles.wrapper} aria-label="Loading page" role="status">
      <div style={styles.ring} />
      <p style={styles.label}>Loading...</p>
    </div>
  )
}

const styles = {
  wrapper: {
    position:       'fixed',
    inset:          0,
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    background:     '#0a0a0f',
    zIndex:         9999,
    gap:            '1.25rem',
  },
  ring: {
    width:           56,
    height:          56,
    borderRadius:    '50%',
    border:          '3px solid transparent',
    // Gradient ring: purple #6c63ff → cyan #00d4ff
    backgroundImage: 'conic-gradient(from 0deg, #6c63ff, #00d4ff, transparent 75%)',
    backgroundOrigin: 'border-box',
    WebkitMask:      'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'destination-out',
    maskComposite:   'exclude',
    animation:       'spin 0.9s linear infinite',
  },
  label: {
    color:      '#a0a0b0',
    fontSize:   '0.875rem',
    fontFamily: 'inherit',
    margin:     0,
    letterSpacing: '0.05em',
  },
}

// Inject the keyframe once into the document head (avoids a separate CSS file).
if (typeof document !== 'undefined') {
  const id = '__loading-spin-keyframe'
  if (!document.getElementById(id)) {
    const s = document.createElement('style')
    s.id = id
    s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }'
    document.head.appendChild(s)
  }
}

// ─── Scroll-to-top on every navigation ────────────────────────────────────────

/**
 * Resets the window scroll position to (0, 0) whenever the pathname changes.
 * Rendered inside the router so it can read useLocation().
 */
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [pathname])

  return null
}

// ─── Route groups ─────────────────────────────────────────────────────────────

/**
 * Routes where the global Navbar and Footer should be hidden.
 * - Auth pages (/login, /register) have their own full-screen layouts.
 * - Landing (/) has its own built-in Navbar + Footer.
 * - Dashboard (/dashboard) has its own fixed Sidebar + TopHeader.
 */
const AUTH_ROUTES = new Set(['/', '/login', '/register', '/dashboard'])

// ─── App ──────────────────────────────────────────────────────────────────────

/**
 * Root application component.
 *
 * Responsibilities:
 *  - Conditionally renders Navbar / Footer (hidden on auth pages)
 *  - Wraps all route transitions with AnimatePresence (mode="wait") so that
 *    the exiting page fully animates out before the entering page mounts
 *  - Provides a Suspense boundary with a full-screen LoadingFallback for
 *    every lazy-loaded page chunk
 *  - Resets scroll position on route change via ScrollToTop
 */
function App() {
  const location = useLocation()
  const isAuthPage = AUTH_ROUTES.has(location.pathname)

  return (
    <div className="min-h-screen bg-background text-text flex flex-col">
      <ScrollToTop />

      {!isAuthPage && <Navbar />}

      <main className="flex-1">
        {/*
          key={location.pathname} forces AnimatePresence to treat each route
          as a distinct child, triggering exit → enter animations cleanly.
        */}
        <AnimatePresence mode="wait" initial={false}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes location={location} key={location.pathname}>
              {/* ── Public routes ── */}
              <Route path="/"          element={<Landing />}   />
              <Route path="/login"     element={<Login />}     />
              <Route path="/register"  element={<Register />}  />

              {/* ── Authenticated routes ── */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile"   element={<Profile />}   />
              <Route path="/payment"   element={<Payment />}   />

              {/* ── Catch-all 404 ── */}
              <Route path="*"          element={<NotFound />}  />
            </Routes>
          </Suspense>
        </AnimatePresence>
      </main>

      {!isAuthPage && <Footer />}

      {/* AI Chat assistant — visible on all pages */}
      <ChatAgent />
    </div>
  )
}

export default App
